// @ts-ignore
import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { useExecutionDataView } from "@gooddata/sdk-ui";
import { newPositiveAttributeFilter } from "@gooddata/sdk-model";
import * as Md from "../md/full";
import ButtonFilter from "./Filters/ButtonFilter";
import MixButtonFilter from "./Filters/MixButtonFilter";
// Import our custom CSS with GoodData-compatible styles
import "../styles/filters.css";

/**
 * Custom Filter Component - a complete replacement for AttributeFilterButton
 */
const CustomFilterComponent = ({ title, staticElements = [], filter, onApply, isLoading = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedItems, setSelectedItems] = useState([]);
    // Track applied selections to properly style the button
    const [appliedSelections, setAppliedSelections] = useState([]);
    const dropdownRef = useRef(null);
    const hasElements = staticElements && staticElements.length > 0;

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Initialize selected items from filter
    useEffect(() => {
        if (filter?.positiveAttributeFilter?.in?.values) {
            const values = filter.positiveAttributeFilter.in.values;
            setSelectedItems(values);
            setAppliedSelections(values);
        }
    }, [filter]);

    // Toggle dropdown
    const toggleDropdown = () => {
        setIsOpen(!isOpen);
        // When reopening, reset selection to match the applied filter
        if (!isOpen) {
            setSelectedItems([...appliedSelections]);
        }
    };

    // Handle item selection
    const handleItemSelect = (item) => {
        setSelectedItems((prevSelected) => {
            if (prevSelected.includes(item.name)) {
                return prevSelected.filter((i) => i !== item.name);
            } else {
                return [...prevSelected, item.name];
            }
        });
    };

    // Handle "Select All" option
    const handleSelectAll = () => {
        if (selectedItems.length === filteredElements.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(filteredElements.map((item) => item.name));
        }
    };

    // Filter elements based on search term
    const filteredElements = useMemo(() => {
        if (!staticElements) return [];
        if (!searchTerm) return staticElements;

        return staticElements.filter((element) => element.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [staticElements, searchTerm]);

    // Handle apply
    const handleApply = () => {
        const newFilter = newPositiveAttributeFilter(filter?.positiveAttributeFilter?.displayForm?.identifier || filter?.negativeAttributeFilter?.displayForm?.identifier, selectedItems);
        onApply(newFilter, false);
        setAppliedSelections(selectedItems);
        setIsOpen(false);
    };

    // Handle clear - now applies the filter immediately
    const handleClear = () => {
        setSearchTerm("");
        const emptySelections = [];
        setSelectedItems(emptySelections);

        // Apply the filter only if there were previously applied selections
        if (appliedSelections.length > 0) {
            const newFilter = newPositiveAttributeFilter(filter?.positiveAttributeFilter?.displayForm?.identifier || filter?.negativeAttributeFilter?.displayForm?.identifier, emptySelections);
            onApply(newFilter, false);
            setAppliedSelections(emptySelections);
        }
        setIsOpen(false);
    };

    return (
        <div className="custom-filter-container" ref={dropdownRef}>
            {title === "Version Mix" ? <MixButtonFilter onClick={toggleDropdown} isActive={appliedSelections.length > 0} title={isLoading ? "Loading..." : title} /> : <ButtonFilter onClick={toggleDropdown} isActive={appliedSelections.length > 0} title={isLoading ? "Loading..." : title} />}

            {isOpen && (
                <div className="custom-filter-dropdown">
                    <div className="custom-filter-search">
                        <input type="text" value={searchTerm} autoFocus onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="custom-filter-search-input" />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm("")} className="custom-filter-search-clear-button">
                                &#x2715; {/* Unicode 'X' symbol */}
                            </button>
                        )}
                    </div>

                    <div className="custom-filter-items-container">
                        {isLoading ? (
                            <div className="custom-filter-loading">Loading...</div>
                        ) : hasElements ? (
                            <>
                                <div className="gd-checkbox gd-checkbox-custom s-select-all">
                                    <label>
                                        <input type="checkbox" checked={filteredElements.length > 0 && selectedItems.length === filteredElements.length} onChange={handleSelectAll} />
                                        <span className="input-label-text">All ({filteredElements.length})</span>
                                    </label>
                                </div>

                                {filteredElements.map((element) => (
                                    <div key={element.uri} className="gd-checkbox gd-checkbox-custom">
                                        <label>
                                            <input type="checkbox" checked={selectedItems.includes(element.name)} onChange={() => handleItemSelect(element)} />
                                            <span title={element.name} className="input-label-text">
                                                {element.name}
                                            </span>
                                        </label>
                                    </div>
                                ))}
                            </>
                        ) : (
                            <div className="custom-filter-no-data">No data available</div>
                        )}
                    </div>

                    <div className="custom-filter-actions">
                        <div className="custom-filter-status" title={selectedItems.length > 0 ? selectedItems.join(", ") : "None selected"}>
                            is {selectedItems.length === 0 ? "None" : selectedItems.length === filteredElements.length ? "All" : selectedItems.length <= 3 ? selectedItems.join(", ") : `${selectedItems.slice(0, 3).join(", ")}... (${selectedItems.length})`}
                        </div>
                        <div className="custom-filter-button-group">
                            <button onClick={handleClear} className="custom-filter-button-clear">
                                Clear
                            </button>
                            <button onClick={handleApply} className="custom-filter-button-apply">
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default function AttributeFilterWithCustomizations({ slicesBy, filter, title, currentFilter, updateFilter, filterName }) {
    const [isLoading, setIsLoading] = useState(true);

    // Check if there are any measure filters in the current filters
    const hasMeasureFilters = useMemo(() => {
        return currentFilter.some((filter) => filter?.measureValueFilter);
    }, [currentFilter]);

    // If we have measure filters, we use attribute values from version level
    // Otherwise, we use the standard approach which is much faster
    const executionConfig = useMemo(() => {
        if (hasMeasureFilters) {
            // With measure filters
            return {
                seriesBy: [Md.VersionStdCount, Md.ListPrice.Avg, Md.Ps.Avg, Md.Co2malus.Avg, Md.Kw.Avg, Md.Latestm.Avg, Md.Latestw.Avg],
                slicesBy: [slicesBy[0], Md.VersionStd], // Include VersionStd to get version-level data
                filters: currentFilter,
            };
        } else {
            // Standard approach
            return {
                seriesBy: [Md.ListPrice.Avg, Md.Ps.Avg, Md.Co2malus.Avg, Md.Kw.Avg, Md.Latestm.Avg, Md.Latestw.Avg],
                slicesBy,
                filters: currentFilter,
            };
        }
    }, [currentFilter, slicesBy, hasMeasureFilters]);

    const { result, status } = useExecutionDataView({ execution: executionConfig }, [executionConfig]);

    // Update loading state
    useEffect(() => {
        setIsLoading(status === "loading" || status === "pending");
    }, [status]);

    // Process the results based on which execution type we used
    const staticElements = useMemo(() => {
        if (!result) return [];

        if (hasMeasureFilters) {
            // Process version-level results
            try {
                const versionSlices = result?.data().slices().toArray();
                if (versionSlices && versionSlices.length > 0) {
                    // Get unique attribute values from versions
                    const attrValues = new Set();
                    versionSlices.forEach((slice) => {
                        const attrValue = slice.sliceTitles()[0]; // The first item is our attribute
                        attrValues.add(attrValue);
                    });

                    // Convert to array of objects in the expected format
                    return Array.from(attrValues).map((name) => ({
                        name,
                        title: name,
                        uri: `uri-${name}`, // URI isn't critical here, just needs to be unique
                    }));
                }
                return [];
            } catch (error) {
                console.error("Error processing version data", error);
                return [];
            }
        } else {
            // Process attribute-level results (standard method)
            const headers = result?.meta()?.allHeaders()?.[0]?.[0];
            if (!headers || headers.length === 0) {
                return [];
            }
            return (
                headers
                    // @ts-ignore
                    .map((m) => ({
                        // @ts-ignore
                        name: m?.attributeHeaderItem?.name,
                        // @ts-ignore
                        title: m?.attributeHeaderItem?.name,
                        // @ts-ignore
                        uri: m?.attributeHeaderItem?.uri,
                    }))
            );
        }
    }, [result, hasMeasureFilters]);

    const onApply = useCallback(
        (filter, _isInverted) => {
            let filterObj = filter;
            if (filter?.negativeAttributeFilter && filter.negativeAttributeFilter.notIn.values.length > 0) {
                const excludedElements = filter.negativeAttributeFilter.notIn.values;
                const selectedElements = staticElements.filter((obj) => !excludedElements.includes(obj.name)).map((obj) => obj.name);
                filterObj = newPositiveAttributeFilter(filter.negativeAttributeFilter.displayForm.identifier, selectedElements);
            }
            updateFilter(filterName, filterObj);
        },
        [filterName, updateFilter, staticElements],
    );

    // Use our custom filter component instead of AttributeFilterButton
    return <CustomFilterComponent title={title} filter={filter} onApply={onApply} staticElements={staticElements} isLoading={isLoading} />;
}
